import {Suspense, useState, useEffect} from "react";
import {useLoader} from "@react-three/fiber";
import {TextureLoader, Texture} from "three";
import React, {useMemo} from "react";
import {GeoBounds, DegreesToTilePos, GetTileSizeMeters, ValidateTileRequest, TilePosToDegrees} from "./@Shared.js";
import {ErrorBoundary} from "web-vcore";
import {observer_mgl_r3} from "../../../../../Utils/ThreeJS/General.js";
import {Rect, Vec2, Vec3} from "../../../../../Utils/ThreeJS/VectorStructs.js";
import {mapTileCacheDB} from "../../../../../Utils/IndexedDB/MapTileCacheDB.js";
import {MapState} from "../../../../../Store/main/maps/@MapState.js";
import {GetFocusL1Area_World} from "../FocusAreaMarker.js";
import {untracked} from "mobx";
import {MapObject, ShapeType} from "../../../../../Server/DB/mapObjects/@MapObject.js";
import {CreateAccessor} from "mobx-graphlink";
import {GetTinyTransparentImage, tinyTransparentImageAsBase64Str, useBase64ImageStrAsTexture} from "../../../../../Utils/ThreeJS/3JSHooks.js";
import * as THREE from "three";
import {DoesRectangleIntersectEllipse} from "../../../../../Utils/ThreeJS/SpaceUtils.js";
import {FocusLevel} from "../../../../../Store/main/maps/@MapState/MapSettings.js";

// temp hard-coded
export const TILE_MAX_REGION_LENGTH_KM = 20;
//export const TILE_MAX_TILE_COUNT_FOR_REGION = 200;
export const TILE_MAX_TILE_COUNT_FOR_REGION = 999_999;
//export const TILE_OSM_DEFAULT_ZOOM_LEVEL = 16;

export interface TileInfo {
	serverX: number;
	serverY: number;
	mapRect_local: Rect;
	mapRect_world: Rect;
}

//export const GetTileInfo = CreateAccessor((x: number, y: number, centerX: number, centerY: number, zoom: number, containerObjPos: Vec2): TileInfo=>{
export function GetTileInfo(serverX: number, serverY: number, centerX_fractional: number, centerY_fractional: number, zoom: number, containerObjPos: Vec2): TileInfo {
	const latitude = TilePosToDegrees(serverX, serverY, zoom).latitude;
	const tileSizeInMeters = GetTileSizeMeters(latitude, zoom);
	const mapRect_local = Rect.FromCenter(
		// the 0.5 offsets are needed because for both container-obj and tiles, its 3js position = its center-point (unlike tile-server, where tile's position = image top-left [eg. north-pole tiles are exactly y=0])
		// (To confirm logic, consider case of only 1 tile: lets say tile's serverX is 0; centerX_fractional would be 0.5; so then, code below gives mapRect_local of {Left: 0, Right: 1}, which is correct)
		new Vec2(
			((serverX + 0.5) - centerX_fractional) * tileSizeInMeters,
			// reverse the y-value, because tile-server has y+ be "toward south pole", whereas map/3d-world has y+ be "toward north pole"
			-((serverY + 0.5) - centerY_fractional) * tileSizeInMeters
		),
		new Vec2(tileSizeInMeters, tileSizeInMeters),
		true
	);
	const mapRect_world = mapRect_local.NewPosition(a=>a.Plus(containerObjPos));
	return {serverX, serverY, mapRect_local, mapRect_world};
}

// Helper function to check if a tile is completely outside the shape
function IsTileOutsideShape(shapeType: ShapeType, shapeBounds: Rect, tileRect: Rect): boolean {
	const halfWidth = shapeBounds.width / 2;
	const halfHeight = shapeBounds.height / 2;

	if (shapeType == ShapeType.rectangle) {
		//return !shapeBounds.Intersects(tileRect); // this works too, but below is more efficient, since we know both rects use the same yPlusMeansUp value
		// Check if tile is completely outside the rectangle
		if (tileRect.Right < shapeBounds.x || tileRect.x > shapeBounds.Right || tileRect.Top < shapeBounds.y || tileRect.y > shapeBounds.Top) {
			return true;
		}
	} else if (shapeType == ShapeType.ellipse) {
		// Use robust ellipse-rectangle intersection check
		const intersects = DoesRectangleIntersectEllipse(tileRect, shapeBounds.Center.x, shapeBounds.Center.y, halfWidth, halfHeight);
		return !intersects;
	}

	return false;
}


export const TileGrid_OSM = observer_mgl_r3((props: {
	mapState: MapState,
	containerObjPos: Vec3,
	bounds: GeoBounds,
	zoom: number,
	opacity: number,
	focusLevel: FocusLevel,
	shapeType?: ShapeType | null,
	shapeSize?: Vec2 | null,
	restrictToShapeBounds?: boolean,
})=>{
	const {mapState, containerObjPos, bounds, zoom, opacity, focusLevel, shapeType, shapeSize, restrictToShapeBounds} = props;
	const rectForFocusLevel = focusLevel == 1 ? GetFocusL1Area_World(mapState, 1) : null;

	const shapeBounds = useMemo(()=>Rect.FromCenter(containerObjPos.ToVec2(), shapeSize?.Cast(Vec2) ?? new Vec2(0, 0), true), [containerObjPos, shapeSize]);

	// Safety validation and tile calculation
	const {tiles, validation} = useMemo(()=>{
		// Validate the request
		const newValidation = ValidateTileRequest(bounds, zoom, TILE_MAX_REGION_LENGTH_KM, TILE_MAX_TILE_COUNT_FOR_REGION);

		// Calculate the center of the geo-bounds (in degrees)
		const centerLat = (bounds.north + bounds.south) / 2;
		const centerLon = (bounds.west + bounds.east) / 2;

		// Convert center to precise tile position (without flooring) - this is our anchor
		const {tileX: centerX_fractional, tileY: centerY_fractional} = DegreesToTilePos(centerLat, centerLon, zoom, false);

		// Calculate tile coordinates for bounds (these use Math.floor)
		const {tileX: westTile, tileY: northTile} = DegreesToTilePos(bounds.north, bounds.west, zoom, true);
		const {tileX: eastTile, tileY: southTile} = DegreesToTilePos(bounds.south, bounds.east, zoom, true);

		console.log("Input bounds:", bounds);
		console.log("Center:", centerLat, centerLon, "-> precise tile:", centerX_fractional, centerY_fractional);
		console.log("Northwest corner:", bounds.north, bounds.west, "-> tile:", westTile, northTile);
		console.log("Southeast corner:", bounds.south, bounds.east, "-> tile:", eastTile, southTile);
		console.log("Working tile that shows buildings: 19298, 24633");

		// Generate tile list
		const tileList: TileInfo[] = [];
		const minTileX = Math.min(westTile, eastTile);
		const maxTileX = Math.max(westTile, eastTile);
		const minTileY = Math.min(northTile, southTile);
		const maxTileY = Math.max(northTile, southTile);

		// loop over Y-values (latitude) first, because the latitude affects tileSizeInMeters value
		const containerObjPos_2d = containerObjPos.ToVec2();
		for (let tileY = maxTileY; tileY >= minTileY; tileY--) { // tile-y increases southwards, so start with max-y (ie. loop from south to north, to match regular direction of processing)
			let loopOnY_break = false;
			for (let tileX = minTileX; tileX <= maxTileX; tileX++) {
				const tileInfo = GetTileInfo(tileX, tileY, centerX_fractional, centerY_fractional, zoom, containerObjPos_2d);

				if (rectForFocusLevel != null) {
					if (tileInfo.mapRect_world.Right < rectForFocusLevel.x) continue; // continue, since we haven't found the true min-tile-x yet
					if (tileInfo.mapRect_world.x > rectForFocusLevel.Right) break; // break; we know we've passed the focus-area horizontally (ie. on this row)
					if (tileInfo.mapRect_world.Top < rectForFocusLevel.y) break; // break x-loop, causing y-loop to continue (we haven't found the true min-tile-y yet)
					if (tileInfo.mapRect_world.y > rectForFocusLevel.Top) {
						// break x-loop, AND tell y-loop to break (we know we've passed the focus-area vertically)
						loopOnY_break = true;
						break;
					}
				}

				// Skip tiles that are completely outside the shape bounds
				if (restrictToShapeBounds && shapeType && shapeBounds && IsTileOutsideShape(shapeType, shapeBounds, tileInfo.mapRect_world)) {
					continue;
				}

				tileList.push(tileInfo);
			}

			if (loopOnY_break) break;
		}

		return {tiles: tileList, validation: newValidation};
	}, [bounds, zoom, containerObjPos, rectForFocusLevel, shapeType, shapeBounds, restrictToShapeBounds]);

	// Optional: Log validation info
	console.log([
		`Loading ${tiles.length} tiles`,
		`(${validation.totalTiles} before cropping -- ${validation.tileCountX}×${validation.tileCountY})`,
		`covering ${validation.widthKm}×${validation.heightKm}km`,
	].join(" "));

	// atm, whenever we have a new area to display, clear all entries in download-queue that have the same zoom level
	// TODO: improve this system, so it works even if there are multiple OSM-tile instances
	if (focusLevel >= 1) {
		mapTileCacheDB.FilterDownloadQueueToOnlyEntriesMatching(entry=>{
			// if the zoom/TileGrid-instance is the same, look for tiles that are no longer in the focus-area
			if (entry.zoom == zoom) {
				const tileStillInFocusArea = tiles.find(tile=>tile.serverX == entry.tileX && tile.serverY == entry.tileY);
				if (tileStillInFocusArea == null) {
					return false; // remove from queue
				}
			}
			return true;
		});
	}

	return (
		<group position={Vec3.zero.To3JPos()}>
			{tiles.map(tile=>{
				// there is some delay before the MapTile comp renders; we want the tiles to start download immediately though, so call it here
				//mapTileCacheDB.LoadAndCacheTile(zoom, tile.x, tile.y);
				untracked(()=>{
					mapTileCacheDB.GetTile_MobX(zoom, tile.serverX, tile.serverY); // accessing MobX cache is enough to trigger download if not already cached
				});

				return (
					//<Suspense key={`${tile.x}-${tile.y}`} fallback={null}>
					<MapTile key={`${tile.serverX}-${tile.serverY}`}
						mapState={mapState}
						tile={tile}
						zoom={zoom}
						opacity={opacity}
						focusLevel={focusLevel}
						shapeType={shapeType}
						shapeBounds={shapeBounds}
						restrictToShapeBounds={restrictToShapeBounds}
					/>
				);
			})}
		</group>
	);
});

interface MapTileProps {
	mapState: MapState;
	tile: TileInfo;
	zoom: number;
	opacity: number;
	focusLevel: FocusLevel;
	shapeType?: ShapeType | null;
	shapeBounds?: Rect | null;
	restrictToShapeBounds?: boolean;
}

export const MapTile = observer_mgl_r3((props: MapTileProps)=>{
	const {mapState, tile, zoom, opacity, focusLevel, shapeType, shapeBounds, restrictToShapeBounds} = props;
	const latitude = TilePosToDegrees(tile.serverX, tile.serverY, zoom).latitude;
	const tileSizeInMeters = GetTileSizeMeters(latitude, zoom);

	const tileData = mapTileCacheDB.GetTile_MobX(zoom, tile.serverX, tile.serverY);

	/*useEffect(()=>{
		// whenever a MapTile is unmounted, remove any pending download-requests for it from the queue (if tile is already cached, this does nothing, which is fine)
		return ()=>{
			mapTileCacheDB.RemoveEntriesFromDownloadQueueMatching(zoom, tileX, tileY);
			if (mapTileCacheDB.downloadQueue.length > 200) {
				g.test1 = 1;
			}
		};
	});*/

	const texture = useBase64ImageStrAsTexture(tileData?.imageData, GetTinyTransparentImage(), undefined, false);

	// Create custom material with shape clipping if needed
	const material = useMemo(()=>{
		if (!restrictToShapeBounds || !shapeType || !shapeBounds || !texture) {
			return (
				<meshBasicMaterial
					map={texture}
					transparent
					opacity={opacity}
				/>
			);
		}

		// Custom shader material that clips to the shape in world space
		const shaderMaterial = new THREE.ShaderMaterial({
			uniforms: {
				map: {value: texture},
				opacity: {value: opacity},
				shapeType: {value: shapeType === ShapeType.ellipse ? 1.0 : 0.0},
				shapeCenterX: {value: shapeBounds.Center.x},
				shapeCenterY: {value: shapeBounds.Center.y},
				shapeHalfWidth: {value: shapeBounds.width / 2},
				shapeHalfHeight: {value: shapeBounds.height / 2}
			},
			vertexShader: `
				varying vec2 vUv;
				varying vec3 vWorldPosition;
				
				void main() {
					vUv = uv;
					// Calculate world position by transforming through model matrix
					vec4 worldPos = modelMatrix * vec4(position, 1.0);
					vWorldPosition = worldPos.xyz;
					gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
				}
			`,
			fragmentShader: `
				uniform sampler2D map;
				uniform float opacity;
				uniform float shapeType;
				uniform float shapeCenterX;
				uniform float shapeCenterY;
				uniform float shapeHalfWidth;
				uniform float shapeHalfHeight;
				
				varying vec2 vUv;
				varying vec3 vWorldPosition;
				
				void main() {
					// Camera looks toward -Z; the map plane lies in the XY plane.
					// 2D map coordinates map directly to world X and Y.
					float worldX = vWorldPosition.x;
					float worldY = vWorldPosition.y;
					
					// Calculate position relative to shape center
					float relativeX = worldX - shapeCenterX;
					float relativeY = worldY - shapeCenterY;
					
					// Check if fragment is inside the shape
					bool isInside = true;
					
					if (shapeType > 0.5) {
						// Ellipse
						float normalizedX = relativeX / shapeHalfWidth;
						float normalizedY = relativeY / shapeHalfHeight;
						isInside = (normalizedX * normalizedX + normalizedY * normalizedY) <= 1.0;
					} else {
						// Rectangle
						isInside = abs(relativeX) <= shapeHalfWidth && abs(relativeY) <= shapeHalfHeight;
					}
					
					if (!isInside) {
						discard;
					}
					
					vec4 texColor = texture2D(map, vUv);
					gl_FragColor = vec4(texColor.rgb, texColor.a * opacity);
				}
			`,
			transparent: true,
			side: THREE.FrontSide
		});

		return <primitive object={shaderMaterial} attach="material"/>;
	}, [shapeType, shapeBounds, restrictToShapeBounds, texture, opacity]);

	// We use the center of the rect as the 3js "position" here! Because ThreeJS's planeGeometry is centered at the mesh's/object's (0,0).
	// Also, we add a slight Z-offset per focus-level to avoid z-fighting.
	const position: [number, number, number] = [tile.mapRect_local.Center.x, tile.mapRect_local.Center.y, 0.01 + (0.01 * focusLevel)];

	if (!texture) return null;
	return (
		<mesh position={position}>
			<planeGeometry args={[tileSizeInMeters, tileSizeInMeters]} />
			{material}
		</mesh>
	);
});